/* ==========================================================================
   Joy Loyalty page — custom behaviour
   Theme: Pippadu & Ceramide 2026  ·  Template: page.joy-husegprogram

   Does the one thing the Avada Joy app block cannot be configured to do:
   add a "Referrals" card to the Ways to earn grid, ordered right after
   Birthday. Referral is a real earning program in Joy, but the app renders
   it only in its own referral block, never as a way-to-earn card.

   Program icons are NOT touched — they are uploaded per program in the Joy
   admin. The referral card is the exception, because the Joy admin has no
   ways-to-earn icon slot for the referral program; its icon comes from the
   section setting, or the referral program's own uploaded icon if it has one.

   How it stays out of React's way
   -------------------------------
   `#Joy-WayEarn__List` is a React root owned by the app bundle. We therefore:
     · never insert between React's children — the referral card is appended
       last and moved into position with CSS `order` on the flex container
       (the app sets `.joy-way-to-earn-wrapper { display: contents }`, so the
       cards really are flex children of the list);
     · re-apply everything from a MutationObserver, because React re-renders
       the whole list when customer data resolves;
     · disconnect the observer around our own writes so we never loop.

   Program identification
   ----------------------
   Rendered cards carry no program id — only `data-url` / `data-customer`.
   The order and titles come from `window.AVADA_JOY.program.earning`, so we
   match on the rendered title first and fall back to positional index. Each
   card ends up tagged `data-joy-event="..."`, which is also a handy CSS hook.
   ========================================================================== */

(function () {
  'use strict';

  var CONFIG_ID = 'joy-lp-custom-config';
  var LIST_SELECTOR = '#Joy-WayEarn__List';
  var CARD_SELECTOR = '.Joy-WayEarn__Item';
  var REFERRAL_CARD_CLASS = 'joy-lp-referral-card';
  var ORDER_STEP = 10;

  /* Only used when no icon has been uploaded for the referral card. Drawn in
     the app's own idiom: a tinted shape behind a 2px outline, with the accent
     wired to the block's "Icon color" setting via .Avada-Joy_ColorTrigger. */
  var FALLBACK_ICON =
    '<svg class="joy-lp-icn" xmlns="http://www.w3.org/2000/svg" width="56" height="56" ' +
    'viewBox="0 0 56 56" fill="none" aria-hidden="true" focusable="false">' +
    '<circle class="joy-lp-icn__accent Avada-Joy_ColorTrigger" cx="26" cy="18" r="11" fill="currentColor"/>' +
    '<g class="joy-lp-icn__line" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="23" cy="19" r="7.5"/>' +
    '<path d="M9 44a14 14 0 0 1 24-9.8"/>' +
    '<path d="M42 47s-8-5-8-10.2a4.6 4.6 0 0 1 8-3 4.6 4.6 0 0 1 8 3C50 42 42 47 42 47Z"/>' +
    '</g></svg>';

  /* ------------------------------------------------------------------
     Config + small helpers
     ------------------------------------------------------------------ */
  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function joy() {
    return window.AVADA_JOY || null;
  }

  function normalise(text) {
    return (text || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /* Ordered list of the programs the app renders as cards, mirroring its own
     filter: live programs flagged to show on the loyalty page. */
  function earningPrograms() {
    var data = joy();
    var list = (data && data.program && data.program.earning) || [];
    var locale =
      (document.documentElement.getAttribute('lang') || 'en').toLowerCase().split('-')[0];

    return list
      .filter(function (p) {
        return p && p.status !== false && p.showLoyaltyPage !== false && p.expired !== true;
      })
      .map(function (p) {
        var translated = p.translateTitle && p.translateTitle[locale];
        return { event: p.event, title: normalise(translated || p.title) };
      });
  }

  function referralProgram() {
    var data = joy();
    var p = data && data.referralProgram;
    if (!p || p.status === false || p.showLoyaltyPage === false) return null;
    return p;
  }

  /* ------------------------------------------------------------------
     Building the referral card
     Cloned from a live card so every theme-editor setting (radius,
     background, outline, hover preset, column class) comes along for free.
     ------------------------------------------------------------------ */
  function buildReferralCard(sampleCard, cfg, program) {
    var card = sampleCard.cloneNode(true);

    card.classList.add(REFERRAL_CARD_CLASS);
    card.setAttribute('data-joy-event', 'referral');
    card.removeAttribute('data-url');
    card.removeAttribute('data-customer');
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');

    /* The clone arrived carrying another program's icon, whatever its shape. */
    var iconBox = card.querySelector('.Joy-Earn__Item-Icon');
    if (iconBox) setReferralIcon(iconBox, cfg, program);

    var title = card.querySelector('.Joy-Earn__Item-Title');
    if (title) title.textContent = cfg.referralTitle || (program && program.title) || 'Refer a friend';

    var subtitle = card.querySelector('.Joy-Earn__Item-SubTitle');
    if (subtitle) subtitle.textContent = resolveSubtitle(cfg, program);

    /* Any extra line the cloned program had (e.g. a usage limit) is not ours. */
    var limit = card.querySelector('.Joy-Earn__Item-Limit');
    if (limit) limit.parentNode.removeChild(limit);

    card.addEventListener('click', function () {
      goToReferral(cfg);
    });
    card.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        goToReferral(cfg);
      }
    });

    return card;
  }

  /* Mirrors the app's own two shapes: an uploaded icon renders as
     img.Joy-Earn__ItemIcon-CustomImg, a built-in one as an svg inside a div —
     so the app's existing sizing rules apply to both without extra CSS. */
  function setReferralIcon(iconBox, cfg, program) {
    var src = cfg.referralIcon || uploadedIcon(program);

    if (src) {
      var img = document.createElement('img');
      img.className = 'Joy-Earn__ItemIcon-CustomImg';
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      iconBox.innerHTML = '';
      iconBox.appendChild(img);
      return;
    }

    iconBox.innerHTML = '<div>' + FALLBACK_ICON + '</div>';
  }

  function uploadedIcon(program) {
    if (!program || program.iconType === 'default') return '';
    return program.iconImage || '';
  }

  /* The reward amount is edited in the Joy admin, so never bake it into the
     copy. The setting is a template: {points} is the raw number, {reward} the
     app-formatted value. Empty setting falls back to the formatted value. */
  function resolveSubtitle(cfg, program) {
    var template = cfg.referralSubtitle;
    if (!template) return referralReward(program);

    var points = (program && program.customerRewardEarnAmount) || '';
    return template
      .replace(/\{points\}/g, points)
      .replace(/\{reward\}/g, referralReward(program));
  }

  function referralReward(program) {
    if (!program) return '';
    var amount = program.customerRewardEarnAmount;
    if (!amount) return '';
    if (program.customerRewardDiscountType === 'point') return amount + ' points';
    if (program.customerRewardDiscountType === 'percentage') return amount + '% off';
    return String(amount);
  }

  function goToReferral(cfg) {
    if (cfg.referralLink) {
      window.location.href = cfg.referralLink;
      return;
    }

    var target =
      document.querySelector('.Joy-Referral__Dashboard') ||
      document.querySelector('#Joy-Referral') ||
      document.querySelector('.joy-customer-referrals__block');

    /* Signed out, the app renders the referral block empty — send the visitor
       to log in rather than scrolling them to a blank strip of page. */
    var isEmpty = target && !target.firstElementChild && !target.textContent.trim();
    if (!target || isEmpty) {
      window.location.href = cfg.loginUrl || '/account/login';
      return;
    }

    var block = target.closest('.shopify-section') || target;
    block.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
  }

  /* ------------------------------------------------------------------
     The sync pass — idempotent, safe to run on every mutation
     ------------------------------------------------------------------ */
  var observer = null;
  var referralCard = null;

  function sync(cfg) {
    var list = document.querySelector(LIST_SELECTOR);
    if (!list) return;

    var cards = [].slice.call(list.querySelectorAll(CARD_SELECTOR)).filter(function (card) {
      return !card.classList.contains(REFERRAL_CARD_CLASS);
    });
    if (!cards.length) return;

    var programs = earningPrograms();
    var byTitle = {};
    programs.forEach(function (p) {
      if (p.title) byTitle[p.title] = p.event;
    });

    var birthdayOrder = null;

    cards.forEach(function (card, index) {
      var titleEl = card.querySelector('.Joy-Earn__Item-Title');
      var rendered = normalise(titleEl && titleEl.textContent);
      var event = byTitle[rendered] || (programs[index] && programs[index].event) || '';

      card.setAttribute('data-joy-event', event);
      card.style.order = String((index + 1) * ORDER_STEP);

      if (event === 'birthday') birthdayOrder = (index + 1) * ORDER_STEP;
      if (event === 'write_review') applyReviewUrl(card, cfg);
    });

    if (!cfg.showReferralCard) return;

    var program = referralProgram();
    if (!program) return;

    if (!referralCard || !referralCard.isConnected) {
      referralCard = buildReferralCard(cards[0], cfg, program);
    }

    /* After Birthday when there is one, otherwise last. */
    var afterBirthday = cfg.referralPosition !== 'end' && birthdayOrder !== null;
    referralCard.style.order = String(
      afterBirthday ? birthdayOrder + ORDER_STEP / 2 : (cards.length + 1) * ORDER_STEP
    );

    /* The card is appended to the list, not inside `.joy-way-to-earn-wrapper`
       where React keeps its own children — safer, but it means the card sits
       outside the element carrying the block's colour custom properties, so
       carry them across by hand. */
    var wrapper = list.querySelector('.joy-way-to-earn-wrapper');
    var wrapperVars = wrapper && wrapper.getAttribute('style');
    if (wrapperVars && referralCard.getAttribute('data-joy-vars') !== wrapperVars) {
      referralCard.style.cssText = wrapperVars + ';' + referralCard.style.cssText;
      referralCard.setAttribute('data-joy-vars', wrapperVars);
    }

    if (referralCard.parentNode !== list) list.appendChild(referralCard);
  }

  /* ------------------------------------------------------------------
     One destination for "write a review"

     The tile and the floating widget send people to different places. This
     pins the tile — the half the theme owns — to a single URL. The widget
     resolves its own destination inside the app bundle, so that side has to
     be aligned in the Joy admin; there is no theme hook for it.

     stopImmediatePropagation, not just preventDefault: both the app block's
     inline handler (bound on this node) and React's delegated handler would
     otherwise still fire and open the app's own URL.
     ------------------------------------------------------------------ */
  function applyReviewUrl(card, cfg) {
    if (!cfg.reviewUrl) return;

    card.setAttribute('data-url', cfg.reviewUrl);
    if (card.getAttribute('data-joy-review-bound') === '1') return;

    card.addEventListener(
      'click',
      function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.open(cfg.reviewUrl, '_blank', 'noopener');
      },
      true
    );
    card.setAttribute('data-joy-review-bound', '1');
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function run(cfg) {
    var list = document.querySelector(LIST_SELECTOR);
    if (!list) return false;

    if (observer) observer.disconnect();
    sync(cfg);

    observer = observer || new MutationObserver(schedule);
    observer.observe(list, { childList: true, subtree: true });
    return true;
  }

  var pending = null;
  var config = null;

  /* Debounced with setTimeout rather than requestAnimationFrame on purpose:
     rAF is paused whenever the page is not painting (background tab, block
     still below the fold on some engines), which would leave the observer
     armed but never acted on. */
  function schedule() {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      if (config) run(config);
    }, 50);
  }

  function boot() {
    config = readConfig();
    if (!config) return;

    /* The list is a React root mounted by the app bundle, so it can appear well
       after DOMContentLoaded. Poll briefly, then hand over to the observer. */
    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      if (run(config) || attempts > 60) window.clearInterval(timer);
    }, 250);

    document.addEventListener('joy:ready', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
